import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "gc_cookie_consent_v1";

/**
 * Lightweight, accessible cookie consent banner.
 * Required for Google Ads policy in CCPA/GDPR regions and a strong trust
 * signal for any site running advertising/analytics.
 *
 * Two paths: "Accept all" (stores `accepted`) or "Decline non-essential"
 * (stores `essential_only`). Either choice persists in localStorage and the
 * banner does not reappear.
 */
export default function CookieBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (!v) setShow(true);
    } catch {
      // localStorage blocked — render the banner anyway
      setShow(true);
    }
  }, []);

  const persist = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // ignore
    }
    setShow(false);
    // Surface decision globally so analytics scripts can read it
    window.dispatchEvent(new CustomEvent("gc_consent", { detail: value }));
  };

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      data-testid="cookie-banner"
      className="fixed inset-x-3 bottom-3 md:inset-x-auto md:right-5 md:bottom-5 md:max-w-md z-[60]"
    >
      <div className="bg-white text-ink rounded-2xl shadow-[0_24px_60px_-20px_rgba(20,20,50,0.35)] border border-warm p-4 md:p-5">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-full bg-signal/12 text-signal flex items-center justify-center">
            <Cookie className="w-4 h-4" />
          </div>
          <div className="text-sm leading-relaxed">
            <strong className="block text-ink mb-0.5">We use cookies</strong>
            <p className="text-ink-2 text-xs md:text-sm">
              We use a few essential cookies to keep you signed in and protect
              your submission. Optional cookies help us measure traffic and
              improve the site. You can change this anytime in our{" "}
              <Link to="/legal/cookies" className="underline text-signal hover:text-signal-hover">
                cookie policy
              </Link>
              .
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2">
          <button
            type="button"
            data-testid="cookie-decline"
            onClick={() => persist("essential_only")}
            className="flex-1 h-10 rounded-xl text-sm font-semibold border border-warm text-ink hover:bg-canvas-2 transition-colors"
          >
            Essential only
          </button>
          <button
            type="button"
            data-testid="cookie-accept"
            onClick={() => persist("accepted")}
            className="flex-1 h-10 rounded-xl text-sm font-semibold bg-signal text-white hover:bg-signal-hover transition-colors"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
